import React, { useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Input, Button, Spinner, Alert
} from "reactstrap";
import config from "../../../config";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";
const API = config.api.DTS_MARKETPLACE_API;

export interface DnsField {
    field: string;
    label: string;
    placeholder?: string;
    helper?: string;
}
export interface DnsOperation {
    path: string;
    method: string; // "GET" | "POST" etc.
    params: string[];
    result: string;
}
export interface InstallRequire {
    field: string;
    mustEqual: string;
    description?: string;
}
export interface DnsConnectModalProps {
    app: any;
    fields: DnsField[];
    operations: Record<string, DnsOperation>;
    installRequires: InstallRequire[];
    show: boolean;
    onClose: () => void;
    onInstall: (domain: string) => void;
    tenantId: string;
}
type DnsRecord = {
    Type: string;
    Name: string;
    Value: string;
};

function DnsRecordCard({ rec }: { rec: DnsRecord }) {
    const [copied, setCopied] = useState<string | null>(null);

    const copy = async (val: string, which: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(which);
        setTimeout(() => setCopied(null), 1100);
    };

    // Card style: subtle border, shadow, spacing.
    return (
        <div
            className="d-flex flex-column align-items-start justify-content-between gap-2 p-3 mb-2 rounded shadow-sm bg-white border"
            style={{ minHeight: 56 }}
        >
            {/* Type */}
            <div>
                <span className="fw-bold" style={{ fontFamily: "monospace", fontSize: 15 }}>
                    {rec.Type}
                </span>
            </div>
            {/* Name */}
            <div className="flex-grow-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Name</span>
                <Button
                    size="sm"
                    color="link"
                    className="p-0 ms-1"
                    onClick={() => copy(rec.Name, "name")}
                    tabIndex={-1}
                >
                    {copied === "name"
                        ? <i className="ri-check-line" style={{ color: "#27ae60", fontSize: 18 }} />
                        : <i className="ri-file-copy-line" style={{ color: "#8e99ad", fontSize: 17 }} />}
                </Button>
                <div className="d-flex align-items-center">
                    <span style={{ fontFamily: "monospace", fontSize: 14 }}>{rec.Name}</span>
                </div>
            </div>
            {/* Value */}
            <div className="flex-grow-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Value</span>
                <Button
                    size="sm"
                    color="link"
                    className="p-0 ms-1"
                    onClick={() => copy(rec.Value, "value")}
                    tabIndex={-1}
                >
                    {copied === "value"
                        ? <i className="ri-check-line" style={{ color: "#27ae60", fontSize: 18 }} />
                        : <i className="ri-file-copy-line" style={{ color: "#8e99ad", fontSize: 17 }} />}
                </Button>
                <div className="d-flex align-items-center">
                    <span style={{ fontFamily: "monospace", fontSize: 14 }}>{rec.Value}</span>
                </div>
            </div>
        </div>
    );
}

export default function DnsConnectModal(props: DnsConnectModalProps) {
    const {
        app, fields, operations, installRequires, show,
        onClose, onInstall, tenantId
    } = props;
    const token = useSelector((s: ApplicationState) => s.auth.token);

    const [input, setInput] = useState<Record<string, string>>({});
    const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
    const [status, setStatus] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const renderFields = () =>
        fields.map((f) => (
            <div key={f.field} className="mb-3">
                <label className="fw-semibold mb-1">{f.label}</label>
                <Input
                    value={input[f.field] || ""}
                    placeholder={f.placeholder}
                    onChange={e => setInput(i => ({ ...i, [f.field]: e.target.value }))}
                    autoFocus={f.field === "domain"}
                />
                {f.helper && <small className="text-muted">{f.helper}</small>}
            </div>
        ));

    const callOperation = async (opName: keyof typeof operations) => {
        setError(null);
        const op = operations[opName];
        if (!op) return;
        const params = op.params.map(p => `${p}=${encodeURIComponent(input[p] || "")}`).join("&");
        setLoading(true);
        try {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            if (token) headers["token"] = token;
            const res = await fetch(
                `${API}/api/tenants/${tenantId}/integrations/zilter-emails${op.path}?${params}`,
                { headers }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setLoading(false);
            if (opName === "getDnsRecords") setDnsRecords(data.records || []);
            if (opName === "checkDnsStatus") setStatus(data.status || data.state || "");
        } catch (e: any) {
            setError(e?.message || "Network error");
            setLoading(false);
        }
    };

    const canInstall =
        (installRequires?.length === 0) ||
        (status && installRequires?.every(r => status === r.mustEqual));

    return (
        <Modal isOpen={show} centered fullscreen>
            <ModalHeader tag="h4" className="fw-bold border-0">
                <i className="ri-mail-send-line me-2" style={{ fontSize: 26, color: "#444" }} />
                Connect Your Sending Domain
            </ModalHeader>
            <ModalBody style={{ padding: "2rem" }}>
                {renderFields()}

                <Button
                    color="primary"
                    className="mb-3"
                    disabled={!input.domain || loading}
                    onClick={() => callOperation("getDnsRecords")}
                    style={{ minWidth: 180, fontWeight: 500 }}
                >
                    {loading ? <Spinner size="sm" /> : <><i className="ri-shield-keyhole-line me-1" /> Show DNS Records</>}
                </Button>

                {dnsRecords.length > 0 && (
                    <div className="mt-3">
                        <div className="mb-2 fs-6">
                            <i className="ri-shield-check-line me-1" style={{ color: "#10b981" }} />
                            <b>Add these DNS records to your domain’s DNS settings:</b>
                        </div>
                        <div className="d-flex flex-wrap gap-3 mb-3">
                            {dnsRecords.map((rec, i) => (
                                <DnsRecordCard rec={rec} key={i} />
                            ))}
                        </div>
                        <div className="d-flex align-items-center mb-2">
                            <Button
                                color="info"
                                className="me-2"
                                onClick={() => callOperation("checkDnsStatus")}
                                disabled={loading}
                                style={{ minWidth: 120, fontWeight: 500 }}
                            >
                                {loading ? <Spinner size="sm" /> : <><i className="ri-refresh-line me-1" />Refresh Status</>}
                            </Button>
                            <span className="ms-2 fs-6">
                                Status: <b className={status?.toLowerCase() === "success" ? "text-success" : "text-warning"}>
                                    {status ? status.toUpperCase() : "Not checked"}
                                </b>
                            </span>
                        </div>
                    </div>
                )}

                <div className="mb-4 p-3 rounded bg-light border">
                    <div className="fw-semibold mb-1" style={{ fontSize: 16 }}>
                        Why do I need to add DNS records?
                    </div>
                    <div style={{ fontSize: 14 }}>
                        <span>
                            To securely send emails from your own domain (like <b>yourcompany.com</b>), we must prove to mailbox providers that you own the domain.
                            <br />
                            <b>DNS records</b> are like digital “signatures” added at your domain registrar (e.g., GoDaddy, Namecheap). Once you add them, your domain will be verified for sending.
                            <br />
                            <span className="text-muted">
                                This protects your reputation and prevents email spoofing.
                            </span>
                        </span>
                        <ul className="mt-2" style={{ fontSize: 13 }}>
                            <li>
                                <b>Step 1:</b> Enter your domain (e.g., <b>yourcompany.com</b>) and click <b>Show DNS Records</b>.
                            </li>
                            <li>
                                <b>Step 2:</b> Copy & add each record below to your DNS settings at your registrar.
                            </li>
                            <li>
                                <b>Step 3:</b> Click <b>Refresh Status</b> to check verification. When status is <b className="text-success">SUCCESS</b>, you're done!
                            </li>
                        </ul>
                    </div>
                </div>



                {error && <Alert color="danger" className="mt-2">{error}</Alert>}
            </ModalBody>
            <ModalFooter className="border-0">
                <Button color="secondary" onClick={onClose} style={{ minWidth: 120 }}>Cancel</Button>
                <Button
                    color="success"
                    disabled={!canInstall}
                    onClick={() => onInstall(input.domain)}
                    style={{ minWidth: 120, fontWeight: 500 }}
                >
                    <i className="ri-link-unlink me-1" /> Connect
                </Button>
            </ModalFooter>
        </Modal>
    );
}
